import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminShell from "@/components/feature/AdminShell";
import ProgressBar from "@/components/base/ProgressBar";
import {
  fetchAdminStats,
  fetchAuditLog,
  ROLE_LABELS,
  type AdminStats,
  type AuditLogEntry,
  type UserRole,
} from "@/services/admin";

const statusLabels: Record<string, string> = {
  pending: "Pendientes",
  approved: "Aprobados",
  discarded: "Descartados",
  duplicate: "Duplicados",
};

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Ahora";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} d`;
}

const roleOrder: UserRole[] = ["admin", "socio", "colaborador"];

export default function AdminResumen() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([fetchAdminStats(), fetchAuditLog(8)])
      .then(([s, log]) => {
        if (!active) return;
        setStats(s);
        setAuditLog(log);
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : "No se pudo cargar el panel.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <AdminShell title="Resumen" description="Vista general del equipo, la prospección y el cumplimiento.">
      {loading ? (
        <div className="bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
          <p className="mt-2">Cargando datos reales desde Supabase…</p>
        </div>
      ) : error ? (
        <div className="bg-background-100 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">
          {error}
        </div>
      ) : stats ? (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-background-100 border border-background-200 rounded-2xl p-4">
              <div className="w-9 h-9 rounded-xl bg-primary-500/15 text-primary-600 flex items-center justify-center">
                <i className="ri-team-line"></i>
              </div>
              <div className="mt-3 text-2xl font-heading font-extrabold text-foreground-950">{stats.totalUsers}</div>
              <div className="text-xs text-foreground-600">Usuarios en el equipo</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {roleOrder.map((role) => {
                  const count = stats.usersByRole.find((r) => r.role === role)?.count ?? 0;
                  if (!count) return null;
                  return (
                    <span key={role} className="text-[10px] font-semibold bg-background-200 text-foreground-700 px-2 py-0.5 rounded-full">
                      {ROLE_LABELS[role]}: {count}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-2xl p-4">
              <div className="w-9 h-9 rounded-xl bg-accent-500/15 text-accent-600 flex items-center justify-center">
                <i className="ri-radar-line"></i>
              </div>
              <div className="mt-3 text-2xl font-heading font-extrabold text-foreground-950">{stats.totalProspects}</div>
              <div className="text-xs text-foreground-600">Prospectos capturados</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {stats.prospectsByStatus.slice(0, 3).map((s) => (
                  <span key={s.status} className="text-[10px] font-semibold bg-background-200 text-foreground-700 px-2 py-0.5 rounded-full">
                    {statusLabels[s.status] ?? s.status}: {s.count}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-2xl p-4">
              <div className="w-9 h-9 rounded-xl bg-secondary-500/15 text-secondary-600 flex items-center justify-center">
                <i className="ri-graduation-cap-line"></i>
              </div>
              <div className="mt-3 text-2xl font-heading font-extrabold text-foreground-950">{stats.avgCourseProgress}%</div>
              <div className="text-xs text-foreground-600">Progreso promedio en cursos</div>
              <div className="mt-2"><ProgressBar value={stats.avgCourseProgress} color="secondary" /></div>
            </div>

            <div className="bg-background-100 border border-background-200 rounded-2xl p-4">
              <div className="w-9 h-9 rounded-xl bg-primary-500/15 text-primary-600 flex items-center justify-center">
                <i className="ri-brain-line"></i>
              </div>
              <div className="mt-3 text-2xl font-heading font-extrabold text-foreground-950">{stats.quizCompletedCount}</div>
              <div className="text-xs text-foreground-600">Tests de personalidad completados</div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => navigate("/app/admin/usuarios")}
              className="text-left bg-background-100 border border-background-200 rounded-2xl p-4 flex items-center gap-4 hover:border-primary-400 cursor-pointer transition-colors"
            >
              <span className="w-11 h-11 rounded-xl bg-primary-500/15 text-primary-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-settings-line text-xl"></i>
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-foreground-950">Gestionar usuarios</div>
                <div className="text-xs text-foreground-600">Roles y permisos del equipo</div>
              </div>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xl"></i>
            </button>
            <button
              onClick={() => navigate("/app/admin/cumplimiento")}
              className="text-left bg-background-100 border border-background-200 rounded-2xl p-4 flex items-center gap-4 hover:border-primary-400 cursor-pointer transition-colors"
            >
              <span className="w-11 h-11 rounded-xl bg-accent-500/15 text-accent-600 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-xl"></i>
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-foreground-950">Cumplimiento</div>
                <div className="text-xs text-foreground-600">
                  {stats.activeComplianceRules} reglas · {stats.activeApprovedMessages} mensajes aprobados
                </div>
              </div>
              <i className="ri-arrow-right-s-line text-foreground-400 text-xl"></i>
            </button>
          </div>

          {/* Audit log feed */}
          <div className="mt-6 bg-background-100 border border-background-200 rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-foreground-950">Actividad reciente</h2>
              <span className="text-[11px] text-foreground-500">Se registra automáticamente</span>
            </div>
            {auditLog.length === 0 ? (
              <p className="mt-3 text-sm text-foreground-500">Todavía no hay actividad registrada.</p>
            ) : (
              <ul className="mt-3 divide-y divide-background-200">
                {auditLog.map((entry) => (
                  <li key={entry.id} className="py-3 flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-background-200 text-foreground-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-history-line text-sm"></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-foreground-800">{entry.summary}</div>
                      <div className="text-[11px] text-foreground-500 mt-0.5">
                        {entry.actorName ?? "Sistema"} · {timeAgo(entry.createdAt)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </AdminShell>
  );
}
